import axios from "axios";
import { getToken } from "../utils/constants";
import { API_BASE_URL, API_VERSION } from "../config/appConfig";

// Construct the full API URL
const baseUrl = `${API_BASE_URL}/api/${API_VERSION}`;

interface RequestConfig {
  headers?: {
    [key: string]: string;
  };
}

interface ErrorResponse {
  response?: {
    status: number;
    data?: {
      message?: string;
      code?: string;
      accessLevel?: string;
      graceDaysRemaining?: number;
    };
  };
}

// ✅ Create Axios instance
const api = axios.create({
  baseURL: baseUrl,
  timeout: 15000,
  headers: {
    "Content-Type": "application/json",
  },
});

// ✅ Request Interceptor
api.interceptors.request.use(
  (config) => {
    const token = getToken?.();
    const configWithHeaders = config as RequestConfig;
    
    // Add auth token
    if (token && configWithHeaders.headers) {
      configWithHeaders.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error: Error) => {
    return Promise.reject(error);
  }
);

// ✅ Response Interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error: ErrorResponse & { config?: { url?: string; method?: string; baseURL?: string } }) => {
    if (error.response) {
      const { status } = error.response;
      const url = error.config?.url || '';

      // Log error for debugging
      console.error('API Error:', {
        url: error.config?.url,
        method: error.config?.method,
        status: status,
        message: error.response?.data?.message,
      });

      // Handle specific status codes
      // Don't logout for change-password endpoint (wrong current password returns 401)
      // Don't logout for login-related endpoints
      const skipLogoutUrls = [
        '/auth/change-password',
        '/auth/login',
        '/auth/verify-otp',
        '/payments/create-booking-order',
        '/payments/verify',
      ];
      const shouldSkipLogout = skipLogoutUrls.some(skipUrl => url.includes(skipUrl));

      if (status === 401 && !shouldSkipLogout) {
        console.warn('Unauthorized access - redirecting to login');
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        window.location.href = "/login";
      }

      if (status === 403) {
        const errorCode = error.response?.data?.code;

        // Handle subscription-related 403s
        if (errorCode === 'SUBSCRIPTION_REQUIRED') {
          console.warn('Subscription required - access blocked');
          window.dispatchEvent(new CustomEvent('subscription-blocked', {
            detail: { message: error.response?.data?.message },
          }));
        } else if (errorCode === 'GRACE_PERIOD_LIMITED') {
          console.warn('Grace period - limited access:', {
            graceDaysRemaining: error.response?.data?.graceDaysRemaining,
          });
          window.dispatchEvent(new CustomEvent('subscription-grace-limited', {
            detail: {
              message: error.response?.data?.message,
              graceDaysRemaining: error.response?.data?.graceDaysRemaining,
            },
          }));
        } else {
          console.error('403 Forbidden - CORS or permissions issue:', {
            url: error.config?.url,
            origin: window.location.origin,
            baseURL: error.config?.baseURL,
          });
        }
      }

      if (status === 404) {
        console.warn('Resource not found:', error.config?.url);
      }

      if (status >= 500) {
        console.error('Server error:', error.response?.data?.message);
      }
    }

    return Promise.reject(error);
  }
);

// Export as both 'api' (default) and 'apiClient' for backward compatibility
export const apiClient = api;
export default api;
